import React from 'react';
import { guid, cn } from '@express-labs/raven-tools';
import Blurb from '../Blurb/Blurb';
import Grid from '../Grid/Grid';
import Column from '../Column/Column';
import Icon, { IconV1IconNames } from '../Icon/Icon';
import Link from '../Link/Link';
import s from './AuthorableNotification.scss';
type CtaType = Array<{
  title: string;
  href?: string;
  onClick?: () => void;
}>;

export interface AuthorableNotificationProps {
  messageType?: string;
  areLinksInline?: boolean;
  onClose?: (ev?: React.ChangeEvent<HTMLInputElement>) => void;
  cta?: CtaType;
  display?: boolean;
  message?: Array<string>;
  title?: string;
  maxLength?: number;
}

type RemainingMessageLengthReturnType = [string, number];

type InlineCTAReturnType = [string, CtaType];

interface AuthorableNotificationState {
  isOpen?: boolean;
}

let instance = 0;

export default class AuthorableNotification extends React.Component<
  AuthorableNotificationProps,
  AuthorableNotificationState
> {
  el: Element | null;
  instance: number;
  message: string;
  lastMessage: string;
  ctaLinks: Array<React.ReactNode>;

  static displayName = 'AuthorableNotification';

  constructor(props: AuthorableNotificationProps) {
    super(props);

    this.state = {
      isOpen: true,
    };
    instance += 1;
    this.instance = instance;
    this.closeNotification = this.closeNotification.bind(this);
    this.message = '';
    this.lastMessage = '';
    this.ctaLinks = [];
    this.el = null;
  }

  getRemainingMessageLength = (
    string: string,
    max: number
  ): RemainingMessageLengthReturnType => {
    let newMax: number = Math.abs(max);
    const text: string = string?.substring(0, max);
    newMax -= string?.length;

    return [text, newMax];
  };

  handleInlineCTA = (
    ctaArray: CtaType,
    max: number,
    messages: Array<string>,
    areLinksInline: boolean
  ): InlineCTAReturnType => {
    let newMax: number = max;
    const newCTAArray: CtaType = [];
    let lastMessage = '';

    if (areLinksInline) {
      lastMessage = messages.slice(-1)[0];
      [lastMessage, newMax] = this.getRemainingMessageLength(lastMessage, max);
    }

    if (newMax <= max && newMax > 0) {
      ctaArray.every((item) => {
        if (item.href !== '') {
          let ctaTitle;
          [ctaTitle, newMax] = this.getRemainingMessageLength(
            item.title,
            newMax
          );
          newCTAArray.push({
            title: ctaTitle,
            href: item.href,
            onClick: item.onClick,
          });
          if (newMax <= 0) {
            return false; // Stops iteration.
          }
        }
        return true; // Mandatory to ".every" property works
      });
    }
    return [lastMessage, newCTAArray];
  };

  closeNotification(): void {
    this.setState(
      {
        isOpen: false,
      },
      () => {
        if (typeof this.props.onClose === 'function') {
          this.props.onClose();
        }
      }
    );
  }

  render() {
    const {
      areLinksInline = false,
      display = false,
      maxLength = 108,
      message = [],
      messageType = '',
      title = '',
    } = this.props;
    const { isOpen } = this.state;
    let { cta = [] } = this.props;

    let containerClassNames = '';
    if (isOpen) {
      containerClassNames = cn(
        messageType !== '' && [
          s[`authorable-${messageType}Notification`],
          s.notification,
        ]
      );
    }

    let icon: IconV1IconNames | null;
    switch (messageType) {
      case 'alert':
        icon = 'errorRed';
        break;
      case 'warning':
        icon = 'warningYellow';
        break;
      case 'acceptance':
        icon = 'checkGreen';
        break;
      case 'info':
        icon = 'alertIconBlack';
        break;
      default:
        icon = null;
        break;
    }

    if (cta && cta.length > 0) {
      let lastMessage: string;
      [lastMessage, cta] = this.handleInlineCTA(
        cta,
        maxLength,
        message,
        areLinksInline
      );
      if (areLinksInline) {
        message[message.length - 1] = lastMessage;
      }
      this.ctaLinks = cta.map((link) => {
        const { href, onClick, title } = link;

        if (onClick) {
          return (
            <Link aria-label={title} as="button" key={guid()} onClick={onClick}>
              {title}
            </Link>
          );
        }
        return (
          <Link aria-label={title} href={href} key={guid()}>
            {title}
          </Link>
        );
      });
    }

    if (areLinksInline) {
      this.lastMessage = message[message.length - 1];
      message.pop();
    }

    const messageElements: React.ReactNode = message.map((text) => (
      <Blurb tag="p" kind="secondary" key={guid()} className={s.messageLine}>
        {text.substring(0, maxLength)}
      </Blurb>
    ));

    if (isOpen && display) {
      return (
        <div
          className={containerClassNames}
          role="dialog"
          aria-describedby={`rvn-note-${this.instance}`}
          aria-label={title}
          data-testid="dialog"
        >
          <Grid bleed>
            <Column direction="row">
              {icon !== null && (
                <div className={s.iconColumn} data-testid="iconColumn">
                  <Icon icon={icon} data-testid="icon" />
                </div>
              )}
              <Column direction="column">
                <div
                  className={s.message}
                  id={`rvn-note-${this.instance}`}
                  data-testid="messageColumn"
                >
                  {messageElements}
                  {(cta.length > 0 || this.lastMessage) && areLinksInline && (
                    <Blurb
                      tag="p"
                      kind="secondary"
                      className={s.ctaLinksInline}
                      data-testid="ctaLinksInline"
                    >
                      {this.lastMessage}
                      {this.ctaLinks}
                    </Blurb>
                  )}
                </div>
                {cta.length > 0 && !areLinksInline && (
                  <Blurb
                    tag="p"
                    kind="secondary"
                    className={s.ctaLinks}
                    data-testid="ctaLinksNotInline"
                  >
                    {this.ctaLinks}
                  </Blurb>
                )}
              </Column>
            </Column>
            <Column span={{ fixed: '1' }}>
              <div className={s.closeableColumn} data-testid="closeableColumn">
                <button
                  type="button"
                  onClick={this.closeNotification}
                  className={s.closeable}
                  // grandfathered id
                  // eslint-disable-next-line react/jsx-no-bind
                  ref={(el) => {
                    this.el = el;
                  }}
                  aria-label="Dismiss Alert"
                  data-testid="closeNotification"
                >
                  <svg
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                    className={s.closeableIcon}
                  >
                    <path
                      stroke="black"
                      strokeWidth="1rem"
                      strokeLinecap="round"
                      d="M 0 0 L 200 200 M 200 0 L 0 200"
                      fill="none"
                    />
                  </svg>
                </button>
              </div>
            </Column>
          </Grid>
        </div>
      );
    }

    return null;
  }
}
